import type { Project } from "../../types/project";
import type { Clip } from "../../types/timeline";
import type { Action, ValidationResult } from "../../types/actions";
import type {
  MutableTimeline,
  MutableClip,
} from "../../utils/immutable-updates";
import { createInverseAction } from "../registry";
import type { ActionHandler } from "../registry";
import { findClipInTracks } from "../clip-lookup";

export function findClip(project: Project, clipId: string): Clip | null {
  return findClipInTracks(project.timeline.tracks, clipId);
}

export function patchClip(
  project: Project,
  clipId: string,
  patch: Partial<MutableClip>,
): boolean {
  const timeline = project.timeline as MutableTimeline;
  let found = false;
  timeline.tracks = timeline.tracks.map((track) => ({
    ...track,
    clips: track.clips.map((clip: MutableClip) => {
      if (clip.id === clipId) {
        found = true;
        return { ...clip, ...patch };
      }
      return clip;
    }),
  }));
  return found;
}

export interface ClipFieldHandlerSpec {
  readonly type: string;
  readonly paramKey: string;
  readonly field: keyof MutableClip;
  readonly transform?: (value: unknown) => unknown;
  readonly validateValue?: (value: unknown) => string | null;
}

export function makeClipFieldHandler(spec: ClipFieldHandlerSpec): ActionHandler {
  return {
    type: spec.type,
    validate(action: Action, project: Project): ValidationResult {
      const params = action.params as Record<string, unknown>;
      const clipId = params.clipId;
      const errors = [];
      if (typeof clipId !== "string" || !findClip(project, clipId)) {
        errors.push({
          code: "CLIP_NOT_FOUND",
          message: `Clip not found: ${String(clipId)}`,
        });
      }
      if (spec.validateValue) {
        const msg = spec.validateValue(params[spec.paramKey]);
        if (msg) errors.push({ code: "INVALID_PARAMS", message: msg });
      }
      return { valid: errors.length === 0, errors };
    },
    apply(action: Action, project: Project): void {
      const params = action.params as Record<string, unknown>;
      const clipId = params.clipId as string;
      const raw = params[spec.paramKey];
      const value = spec.transform ? spec.transform(raw) : raw;
      patchClip(project, clipId, {
        [spec.field]: value,
      } as Partial<MutableClip>);
    },
    invert(action: Action, projectBefore: Project): Action | null {
      const params = action.params as Record<string, unknown>;
      const clipId = params.clipId as string;
      const prior = findClip(projectBefore, clipId);
      if (!prior) return null;
      const priorValue = (prior as unknown as Record<string, unknown>)[
        spec.field as string
      ];
      return createInverseAction(action, spec.type, {
        clipId,
        [spec.paramKey]: priorValue,
      });
    },
  };
}

/**
 * A compound instance has a normal timeline clip as its editable shell, and the
 * persisted instance carries its own copy of the timing. The executor did this
 * in a block after its clip switch, for move, trim, slip and trimToPlayhead,
 * which is why it went missing the first time those types were moved: the code
 * sat nowhere near the cases that depended on it.
 *
 * Call it from any handler that changes a clip's timing, or the inspector, the
 * autosave and the export read coordinates one edit behind. roll and slide did
 * not do it before the move and still do not, since that is a behaviour change
 * rather than part of the move.
 */
export function syncNestedInstance(project: Project, clipId: string): void {
  const updated = findClip(project, clipId);
  const instances = project.nestedInstances;
  if (!updated || !instances?.some((item) => item.id === clipId)) return;
  (project as Project & { nestedInstances: typeof instances }).nestedInstances =
    instances.map((item) =>
      item.id === clipId
        ? {
            ...item,
            trackId: updated.trackId,
            startTime: updated.startTime,
            duration: updated.duration,
            inPoint: updated.inPoint,
            outPoint: updated.outPoint,
            transform: updated.transform,
            volume: updated.volume,
          }
        : item,
    );
}
